import { EmployeeBenefitPermission as Permission } from "./shared/enums/permission.enum";

import getHasPermission from "@/plugins/helpers/__ts/permission/permission.hasPermission.plugin";

export type EmployeeBenefitPermissionList = {
  // Participant Permissions
  isAbleToShowParticipantDetail: () => boolean;
  isAbleToCreateParticipant: () => boolean;
  isAbleToEditParticipant: () => boolean;
  isAbleToDeleteParticipant: () => boolean;
  isAbleToActivateParticipant: () => boolean;
  isAbleToApproveEditParticipant: () => boolean;
  isAbleToAttachCardNumberParticipant: () => boolean;
  isAbleToTerminatePendingParticipant: () => boolean;
  isAbleToTerminateParticipant: () => boolean;
  isAbleToDownloadParticipant: () => boolean;
  isAbleToUploadParticipant: () => boolean;
  isAbleToExportParticipantDataToInsurer: () => boolean;

  // Plan Permissions
  isAbleToShowPlanDetail: () => boolean;
  isAbleToCreatePlan: () => boolean;
  isAbleToEditPlan: () => boolean;
  isAbleToDeletePlan: () => boolean;

  // Benefit Permissions
  isAbleToShowBenefitDetail: () => boolean;

  // Policy Permissions
  isAbleToShowPolicyDetail: () => boolean;
  isAbleToShowPolicyDetailWithCommissions: () => boolean;
  isAbleToCreatePolicy: () => boolean;
  isAbleToUpdatePolicy: () => boolean;
  isAbleToArchivePolicy: () => boolean;
  isAbleToExportPolicy: () => boolean;
  isAbleToShowSocialization: () => boolean;

  // Misc
  isSuperAdmin: () => boolean;
};

export function EmployeeBenefitPermission(
  context,
): EmployeeBenefitPermissionList {
  const { hasPermission } = getHasPermission(context);

  // Participant Permissions
  function isAbleToShowParticipantDetail(): boolean {
    const hasPermissionToShowParticipantDetail = hasPermission(
      Permission.EmployeeView,
    );
    return isSuperAdmin() || hasPermissionToShowParticipantDetail;
  }

  function isAbleToCreateParticipant(): boolean {
    const hasPermissionToCreateParticipant = hasPermission(
      Permission.EmployeeAdd,
    );
    return isSuperAdmin() || hasPermissionToCreateParticipant;
  }

  function isAbleToEditParticipant(): boolean {
    const hasPermissionToEditParticipant = hasPermission(
      Permission.EmployeeEdit,
    );
    return isSuperAdmin() || hasPermissionToEditParticipant;
  }

  function isAbleToDeleteParticipant(): boolean {
    const hasPermissionToDeleteParticipant = hasPermission(
      Permission.EmployeeDelete,
    );
    return isSuperAdmin() || hasPermissionToDeleteParticipant;
  }

  function isAbleToActivateParticipant(): boolean {
    const hasPermissionToActivateParticipant = hasPermission(
      Permission.EmployeeActivate,
    );
    return isSuperAdmin() || hasPermissionToActivateParticipant;
  }

  function isAbleToApproveEditParticipant(): boolean {
    const hasPermissionToApproveEditParticipant = hasPermission(
      Permission.EmployeeApproveEdit,
    );
    return isSuperAdmin() || hasPermissionToApproveEditParticipant;
  }

  function isAbleToAttachCardNumberParticipant(): boolean {
    const hasPermissionToAttachCardNumberParticipant = hasPermission(
      Permission.EmployeeAttachCardNumber,
    );
    return isSuperAdmin() || hasPermissionToAttachCardNumberParticipant;
  }

  function isAbleToTerminatePendingParticipant(): boolean {
    const hasPermissionToTerminatePendingParticipant = hasPermission(
      Permission.EmployeeTerminatePending,
    );
    return isSuperAdmin() || hasPermissionToTerminatePendingParticipant;
  }

  function isAbleToTerminateParticipant(): boolean {
    const hasPermissionToTerminateParticipant = hasPermission(
      Permission.EmployeeTerminate,
    );
    return isSuperAdmin() || hasPermissionToTerminateParticipant;
  }

  function isAbleToDownloadParticipant(): boolean {
    const hasPermissionToDownloadParticipant = hasPermission(
      Permission.EmployeeDownload,
    );
    return isSuperAdmin() || hasPermissionToDownloadParticipant;
  }

  function isAbleToUploadParticipant(): boolean {
    const hasPermissionToUploadParticipant = hasPermission(
      Permission.EmployeeUpload,
    );
    return isSuperAdmin() || hasPermissionToUploadParticipant;
  }

  function isAbleToExportParticipantDataToInsurer(): boolean {
    const hasPermissionToExportParticipant = hasPermission(
      Permission.EmployeeExportDataToInsurer,
    );
    return isSuperAdmin() || hasPermissionToExportParticipant;
  }

  // Plan Permissions
  function isAbleToShowPlanDetail(): boolean {
    const hasPermissionToShowPlanDetail = hasPermission(Permission.PlanView);
    return isSuperAdmin() || hasPermissionToShowPlanDetail;
  }

  function isAbleToCreatePlan(): boolean {
    const hasPermissionToCreatePlan = hasPermission(Permission.PlanAdd);
    return isSuperAdmin() || hasPermissionToCreatePlan;
  }

  function isAbleToEditPlan(): boolean {
    const hasPermissionToEditPlan = hasPermission(Permission.PlanUpdate);
    return isSuperAdmin() || hasPermissionToEditPlan;
  }

  function isAbleToDeletePlan(): boolean {
    const hasPermissionToDeletePlan = hasPermission(Permission.PlanDelete);
    return isSuperAdmin() || hasPermissionToDeletePlan;
  }

  // Benefit Permissions
  function isAbleToShowBenefitDetail(): boolean {
    const hasPermissionToBenefitDetail = hasPermission(Permission.BenefitView);
    return isSuperAdmin() || hasPermissionToBenefitDetail;
  }

  // Policy Permissions
  function isAbleToShowPolicyDetail(): boolean {
    const hasPermissionToPolicyDetail = hasPermission(Permission.PolicyView);
    return isSuperAdmin() || hasPermissionToPolicyDetail;
  }
  function isAbleToShowPolicyDetailWithCommissions(): boolean {
    const hasPermissionToShowPolicyDetailWithCommissions = hasPermission(
      Permission.PolicyViewCommissions,
    );
    return isSuperAdmin() || hasPermissionToShowPolicyDetailWithCommissions;
  }
  function isAbleToCreatePolicy(): boolean {
    const hasPermissionToCreatePolicy = hasPermission(Permission.PolicyAdd);
    return isSuperAdmin() || hasPermissionToCreatePolicy;
  }
  function isAbleToUpdatePolicy(): boolean {
    const hasPermissionToUpdatePolicy = hasPermission(Permission.PolicyUpdate);
    return isSuperAdmin() || hasPermissionToUpdatePolicy;
  }
  function isAbleToArchivePolicy(): boolean {
    const hasPermissionToArchivePolicy = hasPermission(
      Permission.PolicyArchive,
    );
    return isSuperAdmin() || hasPermissionToArchivePolicy;
  }
  function isAbleToExportPolicy(): boolean {
    const hasPermissionToExportPolicy = hasPermission(Permission.PolicyExport);
    return isSuperAdmin() || hasPermissionToExportPolicy;
  }

  function isAbleToShowSocialization(): boolean {
    const hasPermissionToShowSocialization = hasPermission(
      Permission.Socialization,
    );
    return isSuperAdmin() || hasPermissionToShowSocialization;
  }

  // Misc

  function isSuperAdmin(): boolean {
    return hasPermission(Permission.SuperAdmin);
  }

  return {
    isAbleToShowParticipantDetail,
    isAbleToCreateParticipant,
    isAbleToEditParticipant,
    isAbleToDeleteParticipant,
    isAbleToActivateParticipant,
    isAbleToApproveEditParticipant,
    isAbleToAttachCardNumberParticipant,
    isAbleToTerminatePendingParticipant,
    isAbleToTerminateParticipant,
    isAbleToDownloadParticipant,
    isAbleToUploadParticipant,
    isAbleToExportParticipantDataToInsurer,
    isAbleToShowPlanDetail,
    isAbleToCreatePlan,
    isAbleToEditPlan,
    isAbleToDeletePlan,
    isAbleToShowBenefitDetail,
    isAbleToShowPolicyDetail,
    isAbleToShowPolicyDetailWithCommissions,
    isAbleToCreatePolicy,
    isAbleToUpdatePolicy,
    isAbleToArchivePolicy,
    isAbleToExportPolicy,
    isSuperAdmin,
    isAbleToShowSocialization,
  };
}
